import { useEffect, useRef, useState } from "react";

/*
 * Launch-day social proof.
 * - Shows toast notifications (bottom-left) of "recent" purchases.
 * - Returns a `bonus` object that the parent merges into the goal-gauge stats
 *   so the meter visibly climbs.
 * - Active for 4 hours from the visitor's first page load.
 */

const FIRST_NAMES = [
  "Marcus", "Tasha", "Andre", "Keisha", "Jamal", "Tiana", "Devon", "Aaliyah",
  "Tre", "Imani", "Donovan", "Nia", "Malik", "Zora", "Terrell", "Ayana",
  "Darnell", "Janelle", "Reggie", "Brianna", "Cameron", "Latonya", "Khalil",
  "Simone", "Jaylen", "Maya", "Isaiah", "Camille", "Quincy", "Octavia",
  "Stephen", "Monique", "Bryson", "Whitney", "Trevor", "Yolanda", "Damon",
  "Renee", "Tariq", "Naomi", "Elijah", "Aisha", "Brandon", "Vanessa",
  "Lamar", "Adrienne", "Maurice", "Cherise", "Roland", "Tameka",
  "Patrick", "Sarah", "Michael", "Linda", "David", "Karen", "Carlos",
  "Rosa", "Anthony", "Denise",
];

const LAST_INITIALS = [
  "J.", "W.", "B.", "T.", "R.", "M.", "H.", "L.", "G.", "C.",
  "S.", "P.", "D.", "F.", "K.", "N.", "A.", "O.",
];

const CITIES = [
  { c: "Atlanta", s: "GA" }, { c: "Mableton", s: "GA" }, { c: "Decatur", s: "GA" },
  { c: "Charlotte", s: "NC" }, { c: "Raleigh", s: "NC" }, { c: "Durham", s: "NC" },
  { c: "Memphis", s: "TN" }, { c: "Nashville", s: "TN" },
  { c: "Birmingham", s: "AL" }, { c: "Montgomery", s: "AL" },
  { c: "Jackson", s: "MS" }, { c: "Tupelo", s: "MS" },
  { c: "Houston", s: "TX" }, { c: "Dallas", s: "TX" }, { c: "Austin", s: "TX" },
  { c: "New Orleans", s: "LA" }, { c: "Baton Rouge", s: "LA" },
  { c: "Detroit", s: "MI" }, { c: "Flint", s: "MI" },
  { c: "Chicago", s: "IL" }, { c: "Gary", s: "IN" },
  { c: "Cleveland", s: "OH" }, { c: "Columbus", s: "OH" }, { c: "Cincinnati", s: "OH" },
  { c: "Philadelphia", s: "PA" }, { c: "Pittsburgh", s: "PA" },
  { c: "Baltimore", s: "MD" }, { c: "Washington", s: "DC" },
  { c: "Richmond", s: "VA" }, { c: "Norfolk", s: "VA" },
  { c: "Brooklyn", s: "NY" }, { c: "Harlem", s: "NY" }, { c: "Newark", s: "NJ" },
  { c: "Boston", s: "MA" }, { c: "Hartford", s: "CT" },
  { c: "Miami", s: "FL" }, { c: "Orlando", s: "FL" }, { c: "Jacksonville", s: "FL" },
  { c: "Tampa", s: "FL" },
  { c: "St. Louis", s: "MO" }, { c: "Kansas City", s: "MO" },
  { c: "Oakland", s: "CA" }, { c: "Los Angeles", s: "CA" }, { c: "Compton", s: "CA" },
  { c: "Sumter", s: "SC" }, { c: "Columbia", s: "SC" }, { c: "Charleston", s: "SC" },
  { c: "Louisville", s: "KY" }, { c: "Indianapolis", s: "IN" },
  { c: "Milwaukee", s: "WI" }, { c: "Minneapolis", s: "MN" },
];

// -----------------------------------------------------------------
// CAMPAIGN CONFIG — 4-day ghost-sales campaign toward $25,000
// -----------------------------------------------------------------
// Set to the moment the campaign starts (redeploy time).
// The meter smoothly climbs from CAMPAIGN_START_USD → CAMPAIGN_TARGET_USD over 96 hours.
const CAMPAIGN_START_ISO = "2026-07-08T09:00:00Z";
const CAMPAIGN_DURATION_HOURS = 96; // 4 days
const CAMPAIGN_START_USD = 2800; // anchor to current visible momentum
const CAMPAIGN_TARGET_USD = 27500; // "over $25,000"

const CAMPAIGN_START_MS = new Date(CAMPAIGN_START_ISO).getTime();
const CAMPAIGN_END_MS = CAMPAIGN_START_MS + CAMPAIGN_DURATION_HOURS * 3600 * 1000;

// Popup cadence — slower than launch day so the site doesn't feel spammy
const POPUP_INTERVAL_MS = 6 * 60 * 1000; // ~1 every 6 minutes
const POPUP_JITTER_MS = 90 * 1000; // ±90 sec
const DIGITAL_PROBABILITY = 0.55;
const TOAST_VISIBLE_MS = 5500;
// Meter ticks up smoothly every 20 sec to feel "live"
const METER_TICK_MS = 20_000;

function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function makePurchase() {
  const isDigital = Math.random() < DIGITAL_PROBABILITY;
  return {
    id: `p-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    name: `${pick(FIRST_NAMES)} ${pick(LAST_INITIALS)}`,
    city: (() => {
      const c = pick(CITIES);
      return `${c.c}, ${c.s}`;
    })(),
    type: isDigital ? "digital" : "hardcover",
    amount: isDigital ? 1 : 24.99,
  };
}

/**
 * How much of the campaign target has "accumulated" by `nowMs`.
 * Interpolates from CAMPAIGN_START_USD → CAMPAIGN_TARGET_USD across 96 hours.
 * A tiny sine wobble makes the meter feel less mechanical.
 */
function campaignAccumulated(nowMs) {
  const elapsed = nowMs - CAMPAIGN_START_MS;
  if (elapsed <= 0) return CAMPAIGN_START_USD;
  const durMs = CAMPAIGN_END_MS - CAMPAIGN_START_MS;
  const ratio = Math.min(1, elapsed / durMs);
  const base =
    CAMPAIGN_START_USD + ratio * (CAMPAIGN_TARGET_USD - CAMPAIGN_START_USD);
  // ±0.15% wobble so the meter doesn't feel perfectly linear
  const wobbleAmp = (CAMPAIGN_TARGET_USD - CAMPAIGN_START_USD) * 0.0015;
  const wobble = Math.sin(nowMs / 90_000) * wobbleAmp;
  return Math.max(
    CAMPAIGN_START_USD,
    Math.min(CAMPAIGN_TARGET_USD, base + wobble),
  );
}

// Average sale price — used to derive copies-sold from dollar total
const AVG_SALE_USD =
  DIGITAL_PROBABILITY * 1 + (1 - DIGITAL_PROBABILITY) * 24.99;

export function useSocialProof() {
  const [bonus, setBonus] = useState(() => {
    const raised = campaignAccumulated(Date.now());
    return {
      raised,
      copies: Math.floor(raised / AVG_SALE_USD),
      supporters: [],
    };
  });
  const [toast, setToast] = useState(null);
  const timerRef = useRef(null);
  const toastTimerRef = useRef(null);
  const meterTickRef = useRef(null);

  useEffect(() => {
    // Seed a few "recent supporters" for the sidebar chips
    const seed = Array.from({ length: 5 }, () => {
      const p = makePurchase();
      return { name: p.name, kind: "purchase", amount_usd: p.amount };
    });
    setBonus((b) => ({ ...b, supporters: seed }));

    // --- Meter tick: smoothly update the accumulated total ---
    meterTickRef.current = setInterval(() => {
      const raised = campaignAccumulated(Date.now());
      setBonus((b) => ({
        ...b,
        raised,
        copies: Math.floor(raised / AVG_SALE_USD),
      }));
    }, METER_TICK_MS);

    // --- Live popup loop (independent visual FOMO) ---
    function fire() {
      const now = Date.now();
      // Only fire while campaign is active
      if (now > CAMPAIGN_END_MS) return;
      const p = makePurchase();
      setBonus((b) => ({
        ...b,
        supporters: [
          { name: p.name, kind: "purchase", amount_usd: p.amount },
          ...b.supporters,
        ].slice(0, 5),
      }));
      setToast(p);
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
      toastTimerRef.current = setTimeout(() => {
        setToast((cur) => (cur && cur.id === p.id ? null : cur));
      }, TOAST_VISIBLE_MS);

      const next =
        POPUP_INTERVAL_MS - POPUP_JITTER_MS +
        Math.random() * POPUP_JITTER_MS * 2;
      timerRef.current = setTimeout(fire, next);
    }
    // First popup 20-40 sec after load so the meter reads first
    const firstDelay = 20_000 + Math.random() * 20_000;
    timerRef.current = setTimeout(fire, firstDelay);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
      if (meterTickRef.current) clearInterval(meterTickRef.current);
    };
  }, []);

  return { bonus, toast };
}

export function SocialProofToast({ toast }) {
  if (!toast) return null;
  const isHardcover = toast.type === "hardcover";
  const initial = toast.name.charAt(0);
  return (
    <div className="social-proof-toast" data-testid="social-proof-toast">
      <div className="spt-avatar" aria-hidden>
        {initial}
      </div>
      <div className="spt-body">
        <div className="spt-line-1">
          <strong>{toast.name}</strong>
          <span className="spt-city"> · {toast.city}</span>
        </div>
        <div className="spt-line-2">
          just bought{" "}
          <span className={`spt-pill ${isHardcover ? "is-hardcover" : "is-digital"}`}>
            {isHardcover ? "Hardcover · $24.99" : "Digital · $1"}
          </span>
        </div>
        <div className="spt-line-3">a few seconds ago</div>
      </div>
    </div>
  );
}
